/**
 * Utilidades para validación y manejo de coordenadas geográficas
 * 
 * IMPORTANTE: Este código debe mantenerse sincronizado con:
 * backend/app/api/routers/email_router.py (función validar_coordenadas)
 * 
 * Rangos válidos para la región (solo para validación de mapas):
 * - Latitud: 30 a 50 (cubre España y regiones circundantes)
 * - Longitud: -10 a +10 (desde oeste de Portugal hasta este de España)
 */

/**
 * Valida que las coordenadas existan y sean números válidos
 * Esta validación es básica y no verifica rangos geográficos
 * 
 * @param {number} lat - Latitud
 * @param {number} lng - Longitud
 * @returns {boolean} true si las coordenadas son números válidos
 */
export const validarCoordenadas = (lat, lng) => {
  return lat != null && lng != null && !isNaN(lat) && !isNaN(lng);
};

/**
 * Valida que las coordenadas estén dentro de los rangos permitidos para visualización en mapa
 * Si detecta que están invertidas, las intercambia automáticamente
 * Esta validación solo debe usarse antes de representar coordenadas en un mapa
 * 
 * @param {number} lat - Latitud
 * @param {number} lng - Longitud
 * @param {string} nombre - Nombre descriptivo para logging (opcional)
 * @returns {{validas: boolean, lat: number, lng: number}} Objeto con validación y coordenadas corregidas
 */
export const validarCoordenadasParaMapa = (lat, lng, nombre = "coordenada") => {
  if (!validarCoordenadas(lat, lng)) {
    return { validas: false, lat, lng };
  }

  // Validar latitud (30-50) y longitud (-10 a +10)
  const latEnRango = lat >= 30 && lat <= 50;
  const lngEnRango = lng >= -10 && lng <= 10;

  // Si ambas están en rango, todo OK
  if (latEnRango && lngEnRango) {
    return { validas: true, lat, lng };
  }

  // Si lat está fuera de rango pero lng está en el rango de latitudes válidas,
  // probablemente están invertidas
  const lngPodriaSerLat = lng >= 30 && lng <= 50;
  const latPodriaSerLng = lat >= -10 && lat <= 10;

  if (!latEnRango && lngPodriaSerLat && latPodriaSerLng) {
    console.warn(`⚠️ [COORDENADAS] ${nombre}: coordenadas invertidas detectadas. Intercambiando: lat=${lng}, lng=${lat}`);
    return { validas: true, lat: lng, lng: lat };
  }

  // Si después de intentar corregir siguen fuera de rango, marcar como inválidas
  console.warn(`⚠️ [COORDENADAS] ${nombre}: coordenadas fuera de rango - lat=${lat} (30-50), lng=${lng} (-10 a +10)`);
  return { validas: false, lat, lng };
};

/**
 * Corrige coordenadas si están invertidas (lat/lng intercambiados)
 * No valida rangos geográficos, solo intercambia si es necesario
 * 
 * @param {number} lat - Latitud potencialmente invertida
 * @param {number} lng - Longitud potencialmente invertida
 * @param {string} nombre - Nombre descriptivo para logging (opcional)
 * @returns {{lat: number, lng: number, intercambiadas: boolean}} Coordenadas corregidas
 */
export const corregirCoordenadasInvertidas = (lat, lng, nombre = "coordenada") => {
  // Si no son números válidos, devolver como están
  if (!validarCoordenadas(lat, lng)) {
    return { lat, lng, intercambiadas: false };
  }

  // Heurística: si la "latitud" tiene valor absoluto > 90 o la "longitud" > 180,
  // probablemente están invertidas
  const latAbsoluta = Math.abs(lat);
  const lngAbsoluta = Math.abs(lng);
  
  // Si lat parece más una longitud y lng parece más una latitud, intercambiar
  if (latAbsoluta > 90 || (lngAbsoluta <= 90 && latAbsoluta > lngAbsoluta && latAbsoluta < 180)) {
    console.log(`✅ [COORDENADAS] Intercambiando ${nombre}: lat=${lng}, lng=${lat}`);
    return { lat: lng, lng: lat, intercambiadas: true };
  }

  return { lat, lng, intercambiadas: false };
};

/**
 * Parsea coordenadas desde un string en formato "lat,lng"
 * 
 * @param {string} coordString - String con coordenadas separadas por coma
 * @param {string} nombre - Nombre descriptivo para logging (opcional)
 * @returns {{lat: number|null, lng: number|null, validas: boolean}} Coordenadas parseadas
 */
export const parsearCoordenadas = (coordString, nombre = "coordenada") => {
  if (!coordString || typeof coordString !== 'string') {
    return { lat: null, lng: null, validas: false };
  }

  const partes = coordString.split(',');
  if (partes.length !== 2) {
    console.warn(`⚠️ [COORDENADAS] ${nombre}: formato inválido "${coordString}"`);
    return { lat: null, lng: null, validas: false };
  }

  const lat = parseFloat(partes[0]);
  const lng = parseFloat(partes[1]);

  if (isNaN(lat) || isNaN(lng)) {
    console.warn(`⚠️ [COORDENADAS] ${nombre}: valores no numéricos "${coordString}"`);
    return { lat: null, lng: null, validas: false };
  }

  // Validar y corregir coordenadas para mapa (incluye detección de inversión)
  const resultado = validarCoordenadasParaMapa(lat, lng, nombre);
  
  return {
    lat: resultado.lat,
    lng: resultado.lng,
    validas: resultado.validas
  };
};

/**
 * Genera URL de Google Maps Directions para visualizar una ruta
 * 
 * @param {number} origenLat - Latitud de origen
 * @param {number} origenLng - Longitud de origen
 * @param {number} destinoLat - Latitud de destino
 * @param {number} destinoLng - Longitud de destino
 * @returns {string|null} URL de Google Maps o null si las coordenadas no son válidas
 */
export const generarUrlGoogleMapsDirections = (origenLat, origenLng, destinoLat, destinoLng) => {
  // Validar y corregir coordenadas si están invertidas
  const origen = validarCoordenadasParaMapa(origenLat, origenLng, "origen");
  const destino = validarCoordenadasParaMapa(destinoLat, destinoLng, "destino");

  // Verificar que ambas coordenadas sean válidas para mapa
  if (!origen.validas || !destino.validas) {
    return null;
  }

  // URL format: https://www.google.com/maps/dir/?api=1&origin=lat,lng&destination=lat,lng&travelmode=driving
  return `https://www.google.com/maps/dir/?api=1&origin=${origen.lat},${origen.lng}&destination=${destino.lat},${destino.lng}&travelmode=driving`;
};
